/**
 * ACP client for [Zed Codex ACP](https://github.com/zed-industries/codex-acp) — standard ACP over stdio via the published npm package.
 */

import type { AcpClientHandle, AcpClientOptions } from './acp-client.js';
import { isCommandOnPath } from './detect-command-on-path.js';
import { createSdkStdioAcpClient } from './sdk/sdk-stdio-acp-client.js';

/** Backend `type` for bridge local agents (align with UI and `resolve-agent-command`). */
export const BACKEND_LOCAL_AGENT_TYPE = 'codex-acp' as const;

export async function detectLocalAgentPresence(): Promise<boolean> {
  return isCommandOnPath('codex');
}

/** Default spawn command when workspace agent type is codex-acp. */
export const DEFAULT_CODEX_ACP_COMMAND = ['npx', '--yes', '@zed-industries/codex-acp'] as const;

export function isCodexAcpCommand(command: string[]): boolean {
  const i = command.indexOf('@zed-industries/codex-acp');
  return i >= 0 && (i === 0 || command[i - 1] === 'npx' || command[i - 1] === 'bunx');
}

/** Codex ACP mode selection is applied after session/new via ACP, not CLI argv. */
export function buildCodexAcpSpawnCommand(
  base: string[],
  _sessionMode?: string,
  _agentConfig?: Record<string, unknown> | null,
): string[] {
  return [...base];
}

/**
 * Spawns the Codex ACP adapter; same wire protocol as other @agentclientprotocol/sdk agents.
 */
export async function createCodexAcpClient(options: AcpClientOptions): Promise<AcpClientHandle> {
  const base =
    options.command?.length && options.command.some((a) => a.includes('codex-acp'))
      ? options.command
      : [...DEFAULT_CODEX_ACP_COMMAND];
  const command = buildCodexAcpSpawnCommand(base, options.sessionMode, options.agentConfig);
  return createSdkStdioAcpClient({
    ...options,
    command,
    /** Codex ACP can ignore `session/cancel`; mirror Claude Code's subprocess fallback. */
    killSubprocessAfterCancelMs: options.killSubprocessAfterCancelMs ?? 2500,
  });
}
